import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { CashierPaymentMethod } from "@/api/cashier";
import { getCurrenciesOptions } from "@/api/cashier/@tanstack/react-query.gen";
import { useAppSelector } from "@/redux/hooks";
import { EFormIds } from "@/utils/enums";

export const useCurrencies = () => {
  const selectedProcessingCurrency = useAppSelector(
    (state) => state.form.formData[EFormIds.Amount]?.selectedProcessingCurrency
  );
  const selectedAccountCurrency = useAppSelector(
    (state) => state.account.selectedAccount?.currency
  );
  const selectedAccountToCurrency = useAppSelector(
    (state) => state.account.selectedToAccount?.currency
  );

  const {
    data: currencies,
    isError,
    error,
    isLoading,
    refetch,
  } = useQuery({
    ...getCurrenciesOptions({}),
    staleTime: "static",
  });

  const getPaymentMethodCurrencies = useCallback(
    (paymentMethod: CashierPaymentMethod | undefined) => {
      if (!paymentMethod || !currencies) {
        return [];
      }

      const currencyCodes =
        paymentMethod.processing_currencies ??
        Object.keys(paymentMethod.settings ?? {});

      return currencyCodes
        .map((currencyCode) => currencies[currencyCode])
        .filter(Boolean);
    },
    [currencies]
  );

  const currencyOptions = useMemo(
    () =>
      currencies
        ? Object.values(currencies).map((currency) => {
            return {
              value: currency.code,
              label: currency.code,
            };
          })
        : [],
    [currencies]
  );

  return {
    isError,
    error,
    isLoading,
    refetch,
    currencies,
    currencyOptions,
    getPaymentMethodCurrencies,
    selectedProcessingCurrency,
    selectedAccountCurrency,
    selectedAccountToCurrency,
  };
};
